import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AuthAnbieter, useAuth } from './auth/AuthKontext.js';
import { useDatenKontext } from './daten/kontext.js';
import { useDaten } from './daten/useDaten.js';
import { Anmelden } from './pages/Anmelden.js';
import { AnmeldenLink } from './pages/AnmeldenLink.js';
import { GebietSeite } from './pages/GebietSeite.js';
import { Datenschutz } from './pages/Datenschutz.js';
import { Hinweise } from './pages/Hinweise.js';
import { Impressum } from './pages/Impressum.js';
import { Lieferung } from './pages/Lieferung.js';
import { NichtGefunden } from './pages/NichtGefunden.js';
import { StandortBericht } from './pages/StandortBericht.js';
import { StandortDetail } from './pages/StandortDetail.js';
import { StandortImport } from './pages/StandortImport.js';
import { Standorte } from './pages/Standorte.js';
import { Uebersicht } from './pages/Uebersicht.js';
import { Vertraulichkeit } from './pages/Vertraulichkeit.js';
import { VerwaltungAuswertung } from './pages/VerwaltungAuswertung.js';
import { VerwaltungLieferstand } from './pages/VerwaltungLieferstand.js';
import { VerwaltungLieferungen } from './pages/VerwaltungLieferungen.js';
import { VerwaltungNutzer } from './pages/VerwaltungNutzer.js';

export function App() {
  return (
    <AuthAnbieter>
      <Rahmen />
    </AuthAnbieter>
  );
}

function Laedt({ text }: { text: string }) {
  return (
    <div className="zustand" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" /> {text}
    </div>
  );
}

function Fehler({ titel, meldung }: { titel: string; meldung: string }) {
  return (
    <div className="zustand zustand-fehler" role="alert">
      <strong>{titel}</strong>
      <p>{meldung}</p>
      <button type="button" className="knopf" onClick={() => window.location.reload()}>
        Neu laden
      </button>
    </div>
  );
}

/** Nur angemeldet, Vertraulichkeit bestätigt; lädt die Gebietsdaten und reicht sie weiter. */
function Geschuetzt() {
  const { zustand } = useAuth();
  const ort = useLocation();
  const ich = zustand.status === 'bereit' ? zustand.ich : null;
  const angemeldet = !!ich?.angemeldet && !ich.nutzer?.ndaErforderlich;
  if (!ich?.angemeldet) {
    return <Navigate to={`/anmelden?weiter=${encodeURIComponent(ort.pathname + ort.search)}`} replace />;
  }
  if (ich.nutzer?.ndaErforderlich) return <Navigate to="/vertraulichkeit" replace />;
  return angemeldet ? <MitDaten /> : null;
}

function MitDaten() {
  const daten = useDaten();
  if (daten.status === 'laedt') return <Laedt text="Daten werden geladen …" />;
  if (daten.status === 'fehler') return <Fehler titel="Die Daten stehen gerade nicht zur Verfügung." meldung={daten.meldung} />;
  return <Outlet context={daten.daten} />;
}

function NurAngemeldet({ children }: { children: JSX.Element }) {
  const { zustand } = useAuth();
  if (zustand.status === 'bereit' && !zustand.ich.angemeldet) return <Navigate to="/anmelden" replace />;
  return children;
}

const UebersichtRoute = () => <Uebersicht daten={useDatenKontext()} />;
const GebietRoute = () => <GebietSeite daten={useDatenKontext()} />;
const HinweiseRoute = () => <Hinweise daten={useDatenKontext()} />;

function Rahmen() {
  const { zustand, abmelden } = useAuth();
  const ich = zustand.status === 'bereit' ? zustand.ich : null;
  const frei = !!ich?.angemeldet && !ich.nutzer?.ndaErforderlich;
  return (
    <div className="layout">
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>
      <header className="kopf">
        <Link to="/" className="kopf-link">
          <img src="/gql-logo.svg" alt="GQL Rechtsanwälte" className="kopf-logo" />
          <span>
            <span className="kopf-titel">Aufschaltgebiete Brandmeldeanlagen</span>
            <span className="kopf-untertitel">Konzessionsgebiete, TAB-Fassungen und Vergleichbarkeit mit Berlin</span>
          </span>
        </Link>
        {ich?.angemeldet && (
          <nav className="kopf-nav" aria-label="Hauptnavigation">
            {frei && (
              <>
                <NavLink to="/" end>
                  Karte
                </NavLink>
                <NavLink to="/standorte" end>
                  {ich.nutzer?.rolle === 'gql_admin' ? 'Standorte' : 'Meine Standorte'}
                </NavLink>
                {ich.nutzer?.rolle === 'gql_admin' ? <NavLink to="/verwaltung">Verwaltung</NavLink> : <NavLink to="/lieferung">Hochladen</NavLink>}
                <NavLink to="/hinweise">Daten und Methodik</NavLink>
              </>
            )}
            <button type="button" className="text-knopf" onClick={() => void abmelden()}>
              Abmelden
            </button>
          </nav>
        )}
      </header>
      <main id="inhalt" className="inhalt">
        {zustand.status === 'laedt' && <Laedt text="Einen Moment …" />}
        {zustand.status === 'fehler' && <Fehler titel="Das Portal ist gerade nicht erreichbar." meldung={zustand.meldung} />}
        {zustand.status === 'bereit' && (
          <Routes>
            <Route path="/anmelden" element={<Anmelden />} />
            <Route path="/anmelden/link" element={<AnmeldenLink />} />
            <Route
              path="/vertraulichkeit"
              element={
                <NurAngemeldet>
                  <Vertraulichkeit />
                </NurAngemeldet>
              }
            />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route element={<Geschuetzt />}>
              <Route path="/" element={<UebersichtRoute />} />
              <Route path="/gebiet/:slug" element={<GebietRoute />} />
              <Route path="/standorte" element={<Standorte />} />
              <Route path="/standorte/import" element={<StandortImport />} />
              <Route path="/standorte/:id" element={<StandortDetail />} />
              <Route path="/standorte/:id/bericht" element={<StandortBericht />} />
              <Route path="/hinweise" element={<HinweiseRoute />} />
              <Route path="/lieferung" element={<Lieferung />} />
              <Route path="/verwaltung" element={<Navigate to="/verwaltung/lieferungen" replace />} />
              <Route path="/verwaltung/lieferungen" element={<VerwaltungLieferungen />} />
              <Route path="/verwaltung/lieferstand" element={<VerwaltungLieferstand />} />
              <Route path="/verwaltung/auswertung" element={<VerwaltungAuswertung />} />
              <Route path="/verwaltung/nutzer" element={<VerwaltungNutzer />} />
            </Route>
            <Route path="*" element={<NichtGefunden />} />
          </Routes>
        )}
      </main>
      <footer className="fuss">
        <span>GQL Gussone Quecke Legal Partnerschaft von Rechtsanwälten mbB</span>
        <span className="fuss-trenner" aria-hidden="true">
          ·
        </span>
        <a href="https://www.gql-partner.de" target="_blank" rel="noreferrer">
          gql-partner.de
        </a>
        <span className="fuss-trenner" aria-hidden="true">
          ·
        </span>
        <Link to="/impressum">Impressum</Link>
        <span className="fuss-trenner" aria-hidden="true">
          ·
        </span>
        <Link to="/datenschutz">Datenschutz</Link>
      </footer>
    </div>
  );
}
